import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import dayjs from '@/config/dayjs';
import EditIcon from '@mui/icons-material/Edit';
import { formatNumberToCurrency } from '@/utils/currency';
import { HoldingViewModel, HoldingDataField } from '@/store/holdingsApi';
import { DiversificationResponse } from '@/store/diversificationsApi';
import { RecordResponse } from '@/models/ServiceTypes';
import HoldingTabs from './HoldingTabs';
import DataField from '../common/DataField';
import EditCostBasisDialog from './EditCostBasisDialog';
import { useTickerUrlFormatter } from '@/hooks/useTickerUrlFormatter';
import { useLocalization } from '@/hooks/useLocalization';
import { formatAppDate, isLiveAsOfDate } from '@/utils/date';
import { holdingAccountLabel } from '@/utils/holdingAccountLabel';

interface HoldingContentProps {
  holding: HoldingViewModel;
  holdingDetails?: HoldingViewModel;
  diversificationData?: DiversificationResponse;
  diversificationDetailsData?: DiversificationResponse;
  alsoHeldHoldingsData?: RecordResponse<HoldingViewModel>;
  isColumnEnabled?: (column: string) => boolean;
  endDate?: string;
  isDiversificationLoading?: boolean;
  diversificationError?: unknown;
  /** Advisor-only: whether the firm's "Show CUSIPs" AGSetting is enabled, already resolved by ClientContent.tsx. Omitted for the end-user flyout. */
  showCusip?: boolean;
  investorId?: number;
}

const HoldingContent: React.FC<HoldingContentProps> = ({
  holding,
  holdingDetails,
  diversificationData,
  alsoHeldHoldingsData,
  isColumnEnabled = () => true,
  endDate,
  isDiversificationLoading,
  diversificationError,
  showCusip = false,
  investorId,
}) => {
  const [isCostBasisOpen, setIsCostBasisOpen] = useState(false);
  const { tickerUrlFormatter } = useTickerUrlFormatter();
  const { t } = useLocalization();

  // Use holdingDetails from RTK Query cache if available, otherwise fall back to holding prop
  // This ensures we always show the most up-to-date data from the cache
  const currentHolding = holdingDetails || holding;

  /**
   * The cost basis edit writes to the live holding, and the dialog is seeded from whatever is on
   * screen. On a past date that would push a historical figure onto today's record, so the edit
   * is withheld rather than trusting the flag the API sent.
   */
  const canEditCostBasis =
    currentHolding.CanEditCostBasis && isLiveAsOfDate(endDate);

  const holdingDataFields = useMemo(() => {
    const data: HoldingDataField[] = [
      {
        title: t('holdingDetails.securityDescription'),
        value: currentHolding.SecurityDescription || null,
      },
      {
        title: t('holdingDetails.balance'),
        value: formatNumberToCurrency(currentHolding.Balance),
      },
      {
        title: t('holdingDetails.ticker'),
        value: currentHolding.Symbol
          ? tickerUrlFormatter(currentHolding.Symbol)
          : null,
      },
      {
        title: t('grid.cusip', 'CUSIP'),
        value: showCusip ? currentHolding.CUSIP || null : null,
      },
      {
        title: t('holdingDetails.lastUpdated'),
        value: currentHolding.UpdatedDate
          ? formatAppDate(currentHolding.UpdatedDate)
          : null,
        tooltip: t('tooltip.lastUpdated.content'),
      },
      {
        title: t('holdingDetails.costBasis'),
        value: formatNumberToCurrency(currentHolding.CostBasis),
        isEditable: canEditCostBasis,
        ...(canEditCostBasis
          ? {
              onClick: () => {
                setIsCostBasisOpen(true);
              },
            }
          : {}),
      },
      {
        title: isColumnEnabled('Quantity')
          ? t('holdingDetails.quantity')
          : t('holdingDetails.shares'),
        value: currentHolding.Quantity
          ? t('holdingDetails.sharesPricedAt', {
              quantity: currentHolding.Quantity.toLocaleString('en-US', {
                maximumFractionDigits: 20,
              }),
              price: formatNumberToCurrency(currentHolding.Price),
            })
          : null,
      },
      {
        title: t('holdingDetails.heldAt'),
        value:
          holdingAccountLabel(currentHolding) || currentHolding.InstitutionName,
      },
      {
        title: t('holdingDetails.maturityDate'),
        value: currentHolding.MaturityDate
          ? dayjs(currentHolding.MaturityDate).format('MM/DD/YYYY')
          : null,
      },
      {
        title: t('holdingDetails.interestRate'),
        value:
          currentHolding.InterestRate !== undefined
            ? `${(currentHolding.InterestRate * 100).toFixed(2)}%`
            : null,
      },
    ];

    // Filter out null/undefined/empty values
    return data.filter((item) => {
      if (
        item.value === null ||
        item.value === undefined ||
        item.value === ''
      ) {
        return false;
      }
      if (typeof item.value === 'number') {
        return !isNaN(item.value);
      }
      if (typeof item.value === 'string') {
        return item.value.trim().length > 0;
      }
      return true;
    });
  }, [
    currentHolding,
    isColumnEnabled,
    tickerUrlFormatter,
    t,
    showCusip,
    canEditCostBasis,
  ]);

  return (
    <Stack
      direction='column'
      px={3}
      py={4}
      gap={5}
    >
      {/* Data Fields Grid */}
      <Box
        display='flex'
        flexWrap='wrap'
        mx={-1}
      >
        {holdingDataFields.map(
          ({ title, value, tooltip, isEditable, onClick }, index) => (
            <DataField
              key={index}
              title={title}
              value={value}
              tooltip={tooltip}
              isEditable={isEditable}
              onClick={onClick}
              editContent={isEditable ? <EditIcon fontSize='small' /> : null}
            />
          ),
        )}
      </Box>

      {/* Tabs Section */}
      <HoldingTabs
        holding={currentHolding}
        diversificationData={diversificationData}
        isDiversificationLoading={isDiversificationLoading}
        diversificationError={diversificationError}
        endDate={endDate}
        alsoHeldHoldingsData={alsoHeldHoldingsData}
        investorId={investorId}
        showCusip={showCusip}
      />
      <EditCostBasisDialog
        open={isCostBasisOpen && canEditCostBasis}
        onClose={() => {
          setIsCostBasisOpen(false);
        }}
        holding={currentHolding}
      />
    </Stack>
  );
};

export default React.memo(HoldingContent);
